import { Box, Card, CardContent, Link as MuiLink, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { BackButton } from "@/components/ui/back-button";

const MODES = [
  {
    to: "/documentation/manage/new/manual",
    title: "Write manually",
    description:
      "Compose a document from scratch — title, rich Markdown content, versioning. Best when you know exactly what you want to say.",
    accent: "primary" as const,
    glyph: "✎",
  },
  {
    to: "/documentation/manage/new/upload",
    title: "Upload files",
    description:
      "Pick one or many files — PDF, Word doc, slide deck, image, or any file. Each becomes its own document under the chosen category. Files land in DigitalOcean Spaces (xop-storage), get text-extracted, and are auto-indexed by the AI gateway.",
    accent: "success" as const,
    glyph: "↑",
  },
  {
    to: "/documentation/manage/new/bulk",
    title: "Bulk upload",
    description:
      "Pick a category, then drag in a whole folder of files. Each file becomes its own document in that category — ideal for loading a batch (e.g. all whitepapers) at once.",
    accent: "info" as const,
    glyph: "⇪",
  },
  {
    to: "/documentation/manage/new/ai",
    title: "Generate with AI",
    description:
      "Pick a doc type and release, describe the topic, and have Claude draft the document for you using the rest of your documentation.",
    accent: "warning" as const,
    glyph: "✦",
  },
] as const;

/** Three-card mode picker. Same shape as the customer-support admin
 * panel but skinned for the dashboard's command-center theme. The path
 * each card leads to triggers the matching creation flow. */
export function DocumentNewPage(): JSX.Element {
  // "+ Add to this release / folder" arrives with `?default_release_id=` /
  // `?category_id=`; every mode below reads the same params, so the choice
  // of HOW to create must not lose WHERE it goes.
  const { search } = useLocation();
  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <Box component="header">
        <BackButton to="/documentation" label="Manage" sx={{ mb: 1.5 }} />
        <Typography component="h2" sx={{ mt: 0.5, fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
          New document
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.disabled" }}>
          Pick how you want to create this document.
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" } }}>
        {MODES.map((m) => (
          <MuiLink
            key={m.to}
            component={RouterLink}
            to={`${m.to}${search}`}
            underline="none"
            sx={{
              display: "block",
              borderRadius: 1,
              "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
              "&:hover .doc-mode-title": { color: "primary.main" },
            }}
          >
            <Card
              sx={{
                height: "100%",
                transition: "border-color 200ms, box-shadow 200ms",
                "&:hover": { borderColor: (t) => alpha(t.palette.primary.main, 0.6), boxShadow: (t) => t.shadows[3] },
              }}
            >
              <CardContent sx={{ display: "grid", gap: 1.5 }}>
                <Box
                  aria-hidden
                  sx={{
                    display: "grid",
                    height: 48,
                    width: 48,
                    placeItems: "center",
                    borderRadius: 1,
                    bgcolor: (t) => alpha(t.palette[m.accent].main, 0.18),
                    color: "text.primary",
                    fontSize: "1.5rem",
                  }}
                >
                  {m.glyph}
                </Box>
                <Typography className="doc-mode-title" component="h3" sx={{ fontSize: "1rem", fontWeight: 600, letterSpacing: "-0.01em", transition: "color 150ms" }}>
                  {m.title}
                </Typography>
                <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: "text.disabled" }}>
                  {m.description}
                </Typography>
              </CardContent>
            </Card>
          </MuiLink>
        ))}
      </Box>
    </Box>
  );
}
